<template>
  <div class="markdown" v-html="renderedContent" />
</template>

<script setup>
import { computed } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
});

const renderedContent = computed(() => {
  const html = marked.parse(preprocessCallouts(props.content || ''));
  const htmlWithScrollableTables = wrapTablesInScrollWrapper(html);
  return DOMPurify.sanitize(htmlWithScrollableTables);
});

function wrapTablesInScrollWrapper(html) {
  return html
    .replace(/<table>/g, '<div class="markdown-table-scroll-wrapper"><table>')
    .replace(/<\/table>/g, '</table></div>');
}

function preprocessCallouts(content) {
  const calloutRegex = /^> \[!(NOTE|TIP|WARNING|IMPORTANT|CAUTION)\]\s*\n((?:> [^\n]*\n?)*)/gm;

  const typeConfig = {
    note: { class: 'callout-note', icon: '💡' },
    tip: { class: 'callout-tip', icon: '💡' },
    warning: { class: 'callout-warning', icon: '⚠️' },
    important: { class: 'callout-important', icon: '❗' },
    caution: { class: 'callout-caution', icon: '⚠️' }
  };

  return content.replace(calloutRegex, (match, type, content) => {
    const cleanContent = content
      .split('\n')
      .map(line => line.replace(/^> /, ''))
      .filter(line => line.trim())
      .join(' ')
      .trim();

    const config = typeConfig[type.toLowerCase()] || typeConfig.note;
    const title = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

    // Parse markdown in the callout content (for bold, italic, etc.)
    const parsedContent = marked.parseInline(cleanContent);

    return `<div class="callout ${config.class}">
      <div class="callout-header">
        <span class="callout-icon">${config.icon}</span>
        <span class="callout-title">${title}</span>
      </div>
      <div class="callout-content"><p>${parsedContent}</p></div>
    </div>\n\n`;
  });
}
</script>

<style lang="scss" scoped>
.markdown {
  line-height: 1.6;

  :deep(h1) {
    font-size: 2em;
    margin-top: 0.67em;
    margin-bottom: 0.67em;
  }

  :deep(h2) {
    font-size: 1.5em;
    margin-top: 0.83em;
    margin-bottom: 0.83em;
  }

  :deep(h3) {
    font-size: 1.17em;
    margin-top: 1em;
    margin-bottom: 1em;
  }

  :deep(p) {
    margin-top: 1em;
    margin-bottom: 1em;
  }

  :deep(ul),
  :deep(ol) {
    margin-top: 1em;
    margin-bottom: 1em;
    padding-left: 40px;
  }

  :deep(li) {
    margin-bottom: 0.5em;
  }

  :deep(strong) {
    font-weight: bold;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(blockquote) {
    margin-left: 0;
    padding-left: 1em;
    border-left: 4px solid $color-grey-9;
    color: $color-grey-5;
  }

  :deep(pre) {
    background-color: $color-grey-12;
    padding: 1em;
    overflow-x: auto;
    border-radius: 4px;
  }

  :deep(code) {
    font-family: monospace;
    background-color: $color-grey-12;
    padding: 0.2em 0.4em;
    border-radius: 3px;
  }

  :deep(img) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1em 0;
    border-radius: 4px;
  }

  :deep(.markdown-table-scroll-wrapper) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 1.5em 0;
  }

  :deep(table) {
    width: 100%;
    min-width: max-content;
    border-collapse: collapse;
  }

  :deep(th),
  :deep(td) {
    border: 1px solid rgba($color-primary, 0.4);
    padding: 8px 12px;
    text-align: left;
  }

  :deep(th) {
    background-color: rgba($color-primary, 0.1);
    font-weight: bold;
  }

  :deep(tr:nth-child(even)) {
    background-color: rgba($color-primary, 0.1);
  }

  /* Callout blocks styling */
  :deep(.callout) {
    border-radius: 8px;
    margin: 1.5em 0;
    padding: 16px;
    border-left: 4px solid;
    border-top: 1px solid;
    border-bottom: 1px solid;
    border-right: 1px solid;

    .callout-header {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-weight: 600;

      .callout-icon {
        margin-right: 8px;
        font-size: 16px;
      }

      .callout-title {
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }

    .callout-content {
      p:first-child {
        margin-top: 0;
      }

      p:last-child {
        margin-bottom: 0;
      }
    }

    &.callout-note {
      background-color: #f6f8fa;
      border-color: #0969da;

      .callout-header {
        color: #0969da;
      }
    }

    &.callout-tip {
      background-color: #f6ffed;
      border-color: #52c41a;

      .callout-header {
        color: #52c41a;
      }
    }

    &.callout-warning {
      background-color: #fffbe6;
      border-color: #faad14;

      .callout-header {
        color: #faad14;
      }
    }

    &.callout-important {
      background-color: #fef7f0;
      border-color: #fa541c;

      .callout-header {
        color: #fa541c;
      }
    }

    &.callout-caution {
      background-color: #fff2f0;
      border-color: #ff4d4f;

      .callout-header {
        color: #ff4d4f;
      }
    }
  }
}
</style>
